import React from 'react'
import { Button, DropdownMenu, Icon, Tooltip } from '@opallabs/lapidary'
import type { PartialMoment } from '../momentTypes'
import { toDayKey } from '../useIsContentOutsideMomentRange'
import { attachMomentPickerStyles as styles } from './styles'

type AttachMomentPickerProps = {
  /** Moments in the workspace for the current sort/interval. */
  moments: Record<string, PartialMoment> | null | undefined
  /** Day the creator is rendered in; moments whose duration covers this day are offered. */
  date: Date
  /** Currently-staged attach target, if any. */
  selectedMomentId: string | null
  /** Stage a moment as the attach target — does not commit creation. */
  onSelect: (moment: PartialMoment) => void
  /** Clear the staged moment so the Create button falls back to creating a new one. */
  onClear: () => void
  onOpen?: () => void
  onClose?: () => void
}

/**
 * Picker for staging an existing moment as the attach target for new
 * content. Selection is state-only — the actual create runs through the
 * parent's Create button. The first item ("Create as new moment") is
 * the default state and clears any staged selection when clicked.
 */
export const AttachMomentPicker: React.FC<AttachMomentPickerProps> = ({
  moments,
  date,
  selectedMomentId,
  onSelect,
  onClear,
  onOpen,
  onClose,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)

  const attachableMoments = React.useMemo(() => {
    if (!moments) return []
    const dayKey = toDayKey(date)
    if (!dayKey) return []
    // Key days the same way the grid places moments (`dateAdjustedForTimezone`
    // via `toDayKey`) so the offered list matches the column the creator sits
    // in; a raw `split('T')` would drift by a day at the timezone boundary.
    return Object.values(moments).filter((moment) => {
      const startKey = toDayKey(moment.scheduled_at)
      if (!startKey) return false
      const endKey = toDayKey(moment.scheduled_end_at) ?? startKey
      return dayKey >= startKey && dayKey <= endKey
    })
  }, [moments, date])

  const hasStagedMoment = Boolean(selectedMomentId)

  return (
    <DropdownMenu.Root
      open={isOpen}
      onOpenChange={(next) => {
        setIsOpen(next)
        if (next) onOpen?.()
        else onClose?.()
      }}
    >
      <Tooltip content="Target moment" side="top" zIndex={10000}>
        <DropdownMenu.Trigger asChild>
          <Button
            ariaLabel="Choose target moment"
            icon={
              <Icon
                ariaHidden
                glyph="moment"
                height={14}
                useHypernova={true}
                width={14}
              />
            }
            variant="tertiary"
            name="attach-moment-picker"
            overrideStyles={styles.button(isOpen, hasStagedMoment)}
            tabIndex={0}
          />
        </DropdownMenu.Trigger>
      </Tooltip>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          style={{
            minWidth: 260,
            maxHeight: 280,
            overflowY: 'auto',
            zIndex: 9999,
          }}
        >
          <DropdownMenu.Label css={styles.header}>
            Target moment
          </DropdownMenu.Label>
          <DropdownMenu.Item onSelect={() => onClear()} css={styles.item}>
            <Icon
              ariaHidden
              glyph={hasStagedMoment ? 'gem-add' : 'checkmark-sm'}
              height={14}
              useHypernova={!hasStagedMoment}
              width={14}
            />
            <span css={styles.itemLabel}>Create as new moment</span>
          </DropdownMenu.Item>
          <div css={styles.separator} role="separator" />
          {attachableMoments.length === 0 ? (
            <div css={styles.emptyState}>No existing moments on this day.</div>
          ) : (
            attachableMoments.map((moment) => {
              const title = moment.title ?? 'Untitled moment'
              const isSelected = moment.id === selectedMomentId
              return (
                <DropdownMenu.Item
                  key={moment.id}
                  onSelect={() => onSelect(moment)}
                  css={styles.item}
                >
                  <Icon
                    ariaHidden
                    glyph={isSelected ? 'checkmark-sm' : 'moment'}
                    height={14}
                    useHypernova={true}
                    width={14}
                  />
                  <span css={styles.itemLabel}>{title}</span>
                </DropdownMenu.Item>
              )
            })
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
